import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import "./EditProjectFormPage.css";

const API_BASE_URL = process.env.REACT_APP_BACKEND_API_URL;

export default function EditProjectFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();

  // ===============================
  // Current User
  // ===============================
  const [currentUser, setCurrentUser] = useState({ fullname: "" });

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setCurrentUser({ fullname: user.fullname });
    }
  }, []);

  // ===============================
  // Form State
  // ===============================
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    projectBudget: "",
    editedBy: "",
  });

  const [imageFile, setImageFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState("");

  // ===============================
  // Fetch Project By ID
  // ===============================
  useEffect(() => {
    const fetchProject = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/projects/${id}`);
        const project = res.data;

        setFormData({
          title: project.title || "",
          description: project.description || "",
          projectBudget: project.projectBudget || "",
          editedBy: currentUser.fullname || "",
        });
      } catch (err) {
        console.error("Failed to fetch project:", err.message);
        setError("Failed to load project.");
      } finally {
        setFetching(false);
      }
    };

    fetchProject();
  }, [id, currentUser.fullname]);

  // ===============================
  // Update editedBy when user loads
  // ===============================
  useEffect(() => {
    if (currentUser.fullname) {
      setFormData((prev) => ({
        ...prev,
        editedBy: currentUser.fullname,
      }));
    }
  }, [currentUser]);

  // ===============================
  // Handlers
  // ===============================
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setImageFile(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const parsedBudget = parseFloat(formData.projectBudget);
    if (isNaN(parsedBudget) || parsedBudget < 0) {
      setError("Please enter a valid budget.");
      setLoading(false);
      return;
    }

    try {
      const form = new FormData();
      form.append("title", formData.title);
      form.append("description", formData.description);
      form.append("projectBudget", parsedBudget);
      form.append("editedBy", formData.editedBy);

      if (imageFile) {
        form.append("image", imageFile);
      }

      await axios.put(`${API_BASE_URL}/projects/${id}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      alert("Project updated successfully!");
      navigate(-1);

    } catch (err) {
      console.error("Update failed:", err.response?.data || err.message);
      setError(
        err.response?.data?.message ||
          "Failed to update project. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  if (fetching) return <div className="edit-project-page">Loading...</div>;

  return (
    <div className="edit-project-page">
      <h1 className="edit-project-title">Edit Project</h1>

      <motion.button
        className="back-btn"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => navigate(-1)}
      >
        &larr; Back
      </motion.button>

      <form className="edit-project-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Title</label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label>Description</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={4}
            required
          />
        </div>

        <div className="form-group">
          <label>Budget (GHS)</label>
          <input
            type="number"
            name="projectBudget"
            value={formData.projectBudget}
            onChange={handleChange}
            min="0"
            step="0.01"
            required
          />
        </div>

        <div className="form-group">
          <label>New Project Image (Optional)</label>
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
          />
        </div>

        <div className="form-group">
          <label>Edited By</label>
          <input
            type="text"
            value={formData.editedBy}
            disabled
          />
        </div>

        {error && <div className="form-error">{error}</div>}

        <motion.button
          type="submit"
          className="edit-project-btn"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          disabled={loading}
        >
          {loading ? "Updating…" : "Update Project"}
        </motion.button>
      </form>
    </div>
  );
}
